import { Button } from "@shared/components/ui/Button";
import theme from "@shared/utils/themeColors";
import React, { useEffect, useState } from "react";
import { InputControl } from "@shared/components/form";
import {
  CreateCategoryData,
  EventCategory,
  UpdateCategoryData,
} from "../types/calendar.types";

interface CategoryFormProps {
  category?: EventCategory;
  onSubmit: (data: CreateCategoryData | UpdateCategoryData) => Promise<void>;
  onDelete?: () => Promise<void>;
  onCancel: () => void;
  isSubmitting: boolean;
}

const COLORS = [
  { name: "Sky", value: "sky" },
  { name: "Indigo", value: "indigo" },
  { name: "Yellow", value: "yellow" },
  { name: "Green", value: "green" },
  { name: "Red", value: "red" },
  { name: "Purple", value: "purple" },
  { name: "Orange", value: "orange" },
  { name: "Pink", value: "pink" },
  { name: "Teal", value: "teal" },
];

const getColorCircleClass = (colorValue: string): string => {
  switch (colorValue) {
    case "sky":
      return "bg-sky-500";
    case "indigo":
      return "bg-indigo-500";
    case "yellow":
      return "bg-yellow-500";
    case "green":
      return "bg-green-500";
    case "red":
      return "bg-red-500";
    case "purple":
      return "bg-purple-500";
    case "orange":
      return "bg-orange-500";
    case "pink":
      return "bg-pink-500";
    case "teal":
      return "bg-teal-500";
    default:
      return "bg-gray-500";
  }
};

const CategoryForm: React.FC<CategoryFormProps> = ({
  category,
  onSubmit,
  onDelete,
  onCancel,
  isSubmitting,
}) => {
  const [name, setName] = useState("");
  const [color, setColor] = useState("sky");
  const [error, setError] = useState<string | null>(null);

  // Initialize form with category data if editing
  useEffect(() => {
    if (category) {
      setName(category.name);
      setColor(category.color);
    }
  }, [category]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    if (!name.trim()) {
      setError("Category name is required");
      return;
    }

    try {
      await onSubmit({ name, color });
      onCancel(); // Close the modal on success
    } catch (err) {
      console.error("Error submitting category form:", err);
      setError("Failed to save category. Please try again.");
    }
  };

  const handleDelete = async () => {
    if (!onDelete) return;

    try {
      await onDelete();
      onCancel(); // Close the modal on success
    } catch (err) {
      console.error("Error deleting category:", err);
      setError("Failed to delete category. Please try again.");
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <InputControl
        id="category-name"
        label="Category Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Enter category name"
        disabled={isSubmitting}
        error={error && !name.trim() ? "Category name is required" : null}
        required
        className={theme.bg.inactive}
      />

      <div>
        <label
          className={`block text-sm font-medium ${theme.text.secondary} mb-1`}
        >
          Color
        </label>
        <div className="grid grid-cols-3 gap-2">
          {COLORS.map((colorOption) => (
            <div
              key={colorOption.value}
              className={`
                flex items-center p-2 rounded cursor-pointer border
                ${
                  color === colorOption.value
                    ? "border-gray-900 dark:border-white"
                    : "border-transparent"
                }
              `}
              onClick={() => setColor(colorOption.value)}
            >
              <div
                className={`w-4 h-4 rounded-full mr-2 ${getColorCircleClass(
                  colorOption.value
                )}`}
              ></div>
              <span className="text-sm">{colorOption.name}</span>
            </div>
          ))}
        </div>
      </div>

      {error && <div className="text-red-500 text-sm mt-2">{error}</div>}

      <div className="flex justify-between mt-4">
        <div>
          {category && onDelete && (
            <Button
              type="button"
              onClick={handleDelete}
              disabled={isSubmitting}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete
            </Button>
          )}
        </div>
        <div className="flex space-x-2">
          <Button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className="bg-gray-200 hover:bg-gray-300 text-gray-800 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-white"
          >
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Saving..." : category ? "Update" : "Create"}
          </Button>
        </div>
      </div>
    </form>
  );
};

export default CategoryForm;
